import React, {useState, useEffect} from 'react';
import Pane from "../../../Components/Pane";
import {cloneDeep} from "lodash";
import {DEFAULT_ERROR_MESSAGE, JOURNEYS, prependJourneyToRouteName} from "../../../constants";
import InvisiblePane from "../../../Components/InvisiblePane";
import ProgressBar from "../../../Components/ProgressBar";
import PrimaryTitle from "../../../Components/PrimaryTitle";
import Button from "../../../Components/Button";
import FormInput from "../../../Components/Forms/FormInput";
import Error from "../../../Components/Error";
import AgentPanel from "../../../Components/AgentPanel";
import BackButton from "../../../Components/BackButton";
import PageLoadingPanel from "../../../Components/PageLoadingPanel";
import Text from "../../../Components/Text";

const BusinessDetailsPage = (props) => {
    const {
        nextPage        = () => {},
        previousPage    = () => {},
        journey         = JOURNEYS.AGENT,
    } = props ;

    const PAGE_TITLE            = 'Business Details';
    const CURRENT_STEP_NUMBER   = 2;
    const TOTAL_STEP_NUMBER     = 4;

    const [data, setData] = useState({
        business_name:                  '',
        business_parent_sector:         '',
        business_sector:                '',
        business_registration_number:   '',
        business_type_id:               '',
        business_number_of_employees:   0,
    });

    const [companyTypes, setCompanyTypes]       = useState(null);
    const [industriesTypes, setIndustriesTypes] = useState(null);
    const [loaded, setLoaded]                   = useState(false);
    const [errors, setErrors]                   = useState(null);
    const [errorMessage, setErrorMessage]       = useState(null);
    const [submitting, setSubmitting]           = useState(false);

    useEffect(() => {
        loadCompanyTypes();
        loadIndustriesTypes();
        loadPageData();
    }, []);

    useEffect(() => {
        if (submitting) {
            submit();
        }
    }, [submitting]);

    const loadCompanyTypes = () => {
        axios.get(route('web-api.select.company-types').toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setCompanyTypes(response.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
            setLoaded(true);
        });
    }

    const loadIndustriesTypes = () => {
        axios.get(route('web-api.select.industries-and-trades').toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setIndustriesTypes(response.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
            setLoaded(true);
        });
    }

    const loadPageData = () => {
        axios.get(route(prependJourneyToRouteName(journey, 'session.business-details.get'), {session: window.session}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setData(response.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoaded(true);
            });
    }

    const submit = () => {
        setErrorMessage(null);
        setErrors(null);

        axios.patch(route(prependJourneyToRouteName(journey, 'session.business-details.update'), {session: window.session}).toString(), data)
            .then((response) => {
                setSubmitting(false);
                nextPage();
            })
            .catch((error) => {
                if (!!error && !!error.response && !!error.response.data) {
                    if (!!error.response.data.message) {
                        setErrorMessage(error.response.data.message);
                    }

                    if (!!error.response.data.errors) {
                        setErrors(error.response.data.errors);
                    }

                    setSubmitting(false);
                } else {
                    console.log(error);
                    setErrorMessage(DEFAULT_ERROR_MESSAGE);
                }
            });
    }

    const updateField = (field, value) => {
        let clone = cloneDeep(data);

        switch (field) {
            case 'business_parent_sector':
                clone[field]                = value;
                clone['business_sector']    = '';
                break;
            default:
                clone[field] = value;
                break;
        }

        setData(clone);
    }

    const getChildSectorOptions = () => {
        if (!data.business_parent_sector) {
            return [];
        }

        for (let i = 0; i < industriesTypes.length; i++) {
            if (parseInt(industriesTypes[i]['value']) === parseInt(data.business_parent_sector)) {
                return industriesTypes[i]['children'];
            }
        }

        return [];
    }

    const renderError = () => {
        if (!errorMessage) {
            return null;
        }

        return <Error message={errorMessage} />
    }

    if (!loaded || !companyTypes || !industriesTypes) {
        return (
            <PageLoadingPanel
                pageTitle={PAGE_TITLE}
                currentStepNumber={CURRENT_STEP_NUMBER}
                totalStepNumber={TOTAL_STEP_NUMBER}
                upperTextComponent={null}
            />
        )
    }

    return(
        <React.Fragment>
            {renderError()}
            <Pane>
                <ProgressBar
                    title={PAGE_TITLE}
                    currentStepNumber={CURRENT_STEP_NUMBER}
                    totalStepNumber={TOTAL_STEP_NUMBER}
                />
                <PrimaryTitle>{PAGE_TITLE}</PrimaryTitle>
                <FormInput
                    name={'business_name'}
                    label={'Full business name'}
                    value={data.business_name}
                    type={'text'}
                    onChange={(value) => updateField('business_name', value)}
                    disabled={submitting || !loaded}
                    error={errors}
                />
                <FormInput
                    name={'business_parent_sector'}
                    label={'Industry'}
                    value={data.business_parent_sector}
                    type={'select'}
                    onChange={(value) => updateField('business_parent_sector', value)}
                    options={industriesTypes}
                    disabled={submitting || !loaded}
                    error={errors}
                />
                <FormInput
                    name={'business_sector'}
                    label={'Trade'}
                    value={data.business_sector}
                    type={'select'}
                    onChange={(value) => updateField('business_sector', value)}
                    options={getChildSectorOptions()}
                    disabled={!data.business_parent_sector || submitting || !loaded}
                    error={errors}
                />
                <FormInput
                    name={'business_registration_number'}
                    label={'Company registration number'}
                    value={data.business_registration_number}
                    type={'text'}
                    onChange={(value) => updateField('business_registration_number', value)}
                    disabled={submitting || !loaded}
                    error={errors}
                />
                <Text style={{marginTop: '-10px', marginBottom: '21px', fontStyle: 'italic', fontSize: '10px'}}>(for use by Limited Liability Companies and Limited Liability Partnerships only)</Text>
                <FormInput
                    name={'business_type_id'}
                    label={'Legal entity'}
                    value={data.business_type_id}
                    type={'select'}
                    onChange={(value) => updateField('business_type_id', value)}
                    options={companyTypes}
                    disabled={submitting || !loaded}
                    error={errors}
                />
                <FormInput
                    name={'business_number_of_employees'}
                    label={'Number of employees'}
                    value={data.business_number_of_employees}
                    type={'number'}
                    min={0}
                    max={30}
                    onChange={(value) => updateField('business_number_of_employees', value)}
                    disabled={submitting || !loaded}
                    error={errors}
                />
                <Text style={{marginTop: '-10px', marginBottom: 0, fontStyle: 'italic', fontSize: '10px'}}>(leave as 0 if Sole Trader)</Text>
            </Pane>
            {renderError()}
            <InvisiblePane style={{paddingTop: '59px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <BackButton
                    disabled={submitting}
                    onClick={() => previousPage()}
                />
                <Button
                    label={submitting ? 'Submitting...' : 'Next'}
                    disabled={submitting}
                    onClick={() => setSubmitting(true)}
                />
            </InvisiblePane>
            <AgentPanel journey={journey}/>
        </React.Fragment>
    )
}

export default BusinessDetailsPage;
